import { type FC, useMemo } from 'react'
import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertCircleIcon, LayoutRightIcon } from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { EditingQaFlagsSpaceImages } from '@/features/photoset/components/editing-qa-flags-images/editing-qa-flags-images'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import { MultipleFlagsPopoverTrigger } from '@/features/photoset/components/flags-popover/multiple-flags-popover-trigger'
import { GalleryEditingQaFlagsModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-editing-qa-flags-modal/gallery-editing-qa-flags-modal'
import { ImagesCounter } from '@/features/photoset/components/images-counter'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useGetPhotosetSpacesList } from '@/features/photoset/queries/use-get-photoset-spaces-list'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import {
	type PhotosetSpace,
	type PhotosetURLParams,
	StepKey
} from '@/features/photoset/types'
import { retrievePhotosetSpacesFromSelectedImages } from '@/features/photoset/utils'
import { useTranslation } from '@/translations/i18n'
import { useCurrentPhotosetSubRoute } from '../hooks/use-current-photoset-sub-route'

const EditingQaTitle = ({
	isLoading,
	selectedImagesCount,
	photosetSpacesImagesCount
}: {
	isLoading: boolean
	selectedImagesCount: number
	photosetSpacesImagesCount: number
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return <Skeleton height={30} width={96} />
	}
	if (selectedImagesCount >= 1) {
		return t('common:x_selected_images', {
			selected: selectedImagesCount,
			count: photosetSpacesImagesCount
		})
	}
	return t('common:x_images', {
		count: photosetSpacesImagesCount
	})
}

interface EditingQaFlagsSpacesProps {
	hasSidebar: boolean
	showSidebar: () => void
}

export const EditingQaFlagsSpaces: FC<EditingQaFlagsSpacesProps> = ({
	hasSidebar,
	showSidebar
}) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const currentPhotosetSubRoute = useCurrentPhotosetSubRoute()
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const images = usePhotosetStore((state) => state.images)
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)

	const photosetQuery = useGetPhotoset(
		{
			id: urlParams.id
		},
		{ enabled: false }
	)

	const photosetSpacesQuery = useGetPhotosetSpacesList(
		{
			urlParams: {
				photosetId: urlParams.id
			}
		},
		{ enabled: false }
	)

	// Select all the images currently listed in each space
	const onSelectAll = (spaces: PhotosetSpace[]) => {
		for (const space of spaces) {
			selectImages(space.images.map((image) => image.id))
		}
	}

	// Force to return an array if photoset spaces is undefined
	const photosetSpaces = useMemo(
		() => photosetSpacesQuery.data ?? [],
		[photosetSpacesQuery]
	)

	// Retrieve selected photoset spaces from selected images
	const selectedPhotosetSpaces = useMemo(() => {
		return retrievePhotosetSpacesFromSelectedImages(images, selectedImages)
	}, [images, selectedImages])

	const photosetSpacesImagesCount = useMemo(
		() =>
			photosetSpaces
				.filter((photosetSpaces) => photosetSpaces.images)
				.flatMap((photosetSpace) => photosetSpace.images).length,
		[photosetSpaces]
	)

	// Handle gallery modal view
	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		currentDetailSpace,
		onExpandImage,
		onReduceImage
	} = useExpandPhotosetImage()

	return (
		<>
			<div className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'>
				<ListHeader
					endSlot={
						<div className='flex grow items-center justify-end gap-2'>
							{currentPhotosetSubRoute === 'qa' &&
								(selectedImages.length >= 1 ? (
									<>
										<ImagesCounter
											count={selectedImages.length}
										/>
										<FlagsPopover
											photosetSpaceIds={
												selectedPhotosetSpaces
											}
											selectedImagesIds={selectedImages}
											stepId={StepKey['editing-qa-flags']}
											trigger={
												<MultipleFlagsPopoverTrigger />
											}
										/>
										<ButtonLegacy
											onClick={() => {
												unselectAll()
											}}
											size='sm'
											type='button'
											variant='tertiary'
										>
											{t('common:unselect_all')}
										</ButtonLegacy>
									</>
								) : (
									photosetSpacesQuery?.data?.length && (
										<ButtonLegacy
											onClick={() =>
												onSelectAll(
													photosetSpacesQuery.data
												)
											}
											size={'sm'}
											type='button'
											variant={'tertiary'}
										>
											{t('common:select_all')}
										</ButtonLegacy>
									)
								))}

							{!hasSidebar && (
								<ButtonLegacy
									onClick={showSidebar}
									shape='square'
									type='button'
									variant='tertiary'
								>
									<LayoutRightIcon />
								</ButtonLegacy>
							)}
						</div>
					}
					size='sm'
					startSlot={
						!photosetSpacesQuery.isLoading && (
							<p>
								{t('common:x_bedrooms', {
									count:
										photosetQuery.data?.assignment.task
											.entity.bedrooms ?? 0
								})}
							</p>
						)
					}
					title={
						<EditingQaTitle
							isLoading={photosetSpacesQuery.isLoading}
							photosetSpacesImagesCount={
								photosetSpacesImagesCount
							}
							selectedImagesCount={selectedImages.length}
						/>
					}
				/>

				{photosetSpacesQuery.isError ? (
					<ErrorBox
						icon={<AlertCircleIcon />}
						title={t('errors:default')}
					/>
				) : (
					<ScrollArea scrollbar={<Scrollbar />}>
						{photosetSpacesQuery.isFetching
							? Array.from(
									{ length: 3 },
									(_, i) => `skeleton-${i}`
								).map((key) => (
									<PhotosetSpaceElement
										asSkeleton
										key={key}
									/>
								))
							: photosetSpaces
									.filter(
										(photosetSpace) =>
											photosetSpace.images.length > 0
									)
									.map((currentPhotosetSpace) => (
										<PhotosetSpaceElement
											endSlot={
												<EditingQaFlagsSpaceImages
													images={
														currentPhotosetSpace.images
													}
													isQaView={
														currentPhotosetSubRoute ===
														'qa'
													}
													onExpandImage={
														onExpandImage
													}
													spaceId={
														currentPhotosetSpace.id
													}
													spaceIndex={photosetSpaces.findIndex(
														(photosetSpace) =>
															photosetSpace.id ===
															currentPhotosetSpace.id
													)}
												/>
											}
											key={currentPhotosetSpace.id}
											spaceName={
												currentPhotosetSpace.space
													.key_name
											}
										/>
									))}
					</ScrollArea>
				)}
			</div>

			{currentDetailImageIndex !== null && currentDetailSpace && (
				<GalleryEditingQaFlagsModal
					currentDetailImageIndex={currentDetailImageIndex}
					currentSpaceId={currentDetailSpace.id}
					currentSpaceName={currentDetailSpace.space.key_name}
					isQaView={currentPhotosetSubRoute === 'qa'}
					onClose={onReduceImage}
					selectableImages={currentDetailSpace.images}
					setCurrentDetailImageIndex={setCurrentDetailImageIndex}
				/>
			)}
		</>
	)
}
